import { cn } from '@/shared/lib/utils';

export interface MVLogoProps {
  readonly className?: string;
  readonly 'aria-hidden'?: boolean;
}

/**
 * MVLogo - M+V Monogram with Depth
 * The M is split into 3 separate stroke groups with gaps:
 * - Left vertical pillar
 * - Center V diagonals (taller, sits on top)
 * - Right vertical pillar
 * Rounded-rect grayish background with drop shadow for depth.
 */
export function MVLogo({ className, 'aria-hidden': ariaHidden }: MVLogoProps) {
  return (
    <svg
      viewBox="0 0 100 100"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={cn('text-current', className)}
      aria-hidden={ariaHidden}
    >
      <defs>
        {/* Background gradient — subtle depth, lighter center */}
        <linearGradient id="bgGradA" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#3a3f4b" />
          <stop offset="50%" stopColor="#434956" />
          <stop offset="100%" stopColor="#2e323b" />
        </linearGradient>
        {/* Drop shadow beneath strokes for depth */}
        <filter id="shadowA" x="-10%" y="-10%" width="120%" height="130%">
          <feDropShadow
            dx="1"
            dy="1.5"
            stdDeviation="2"
            floodColor="#000"
            floodOpacity="0.6"
          />
        </filter>
      </defs>

      {/* Background */}
      <rect width="100" height="100" rx="16" fill="url(#bgGradA)" />
      {/* Subtle inner border for depth */}
      <rect
        x="2"
        y="2"
        width="96"
        height="96"
        rx="14"
        fill="none"
        stroke="#555c6a"
        strokeWidth="0.5"
        opacity="0.4"
      />

      {/* All strokes in one group with drop shadow */}
      <g filter="url(#shadowA)">
        {/* === Center V diagonals (tall, sits on top) === */}
        <path
          d="M 24 15 L 50 58 L 76 15"
          stroke="#e0e4ea"
          strokeWidth="5.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />

        {/* === Left vertical pillar (shorter, below V arm) === */}
        <line
          x1="22"
          y1="34"
          x2="22"
          y2="82"
          stroke="#e0e4ea"
          strokeWidth="5.5"
          strokeLinecap="round"
        />

        {/* === Right vertical pillar (shorter, below V arm) === */}
        <line
          x1="78"
          y1="34"
          x2="78"
          y2="82"
          stroke="#e0e4ea"
          strokeWidth="5.5"
          strokeLinecap="round"
        />
      </g>
    </svg>
  );
}
